import { Control, Controller, FieldValues, Path } from 'react-hook-form'

import { Select } from '@clientbase/clientbase-library'
import { MenuItem } from '@mui/material'

import { formatMoney } from 'utils'

type Installment = {
  amount_final: number
  amount_installment: number
  amount_net: number
  fee: boolean
  fee_perc_credit_card: number
}

type Installments = {
  [key in number]: Installment
}

type InstallmentSelectProps<T extends FieldValues> = {
  control: Control<T, object>
  loading?: boolean
  maxInstallments?: number
  totalValue?: number
  paymentType?: string
  installmentsList?: Installments | null
  fieldName?: string // Nome do campo específico para controlar
  fixed?: boolean // Quantidade fixa de parcelas (o cliente não escolhe)
}

const InstallmentSelect = <T extends FieldValues>({
  control,
  loading = false,
  maxInstallments = 12,
  totalValue = 0,
  paymentType = '',
  installmentsList = null,
  fieldName = 'max_installments',
  fixed = false
}: InstallmentSelectProps<T>) => {
  // Função para calcular o valor da parcela
  const calculateInstallmentValue = (installmentNumber: number): string => {
    // Para cartão de crédito, usar os dados da simulação se disponível
    if (
      (paymentType === 'credit_card_installments' ||
        paymentType === 'credit_card') &&
      installmentsList
    ) {
      const installmentData = installmentsList[installmentNumber]
      if (installmentData) {
        return formatMoney(installmentData.amount_installment)
      }
    }

    // Para PIX, Boleto e outros, fazer divisão simples
    if (totalValue > 0) {
      const installmentValue = totalValue / installmentNumber
      return formatMoney(installmentValue)
    }

    return 'R$ 0,00'
  }

  // Gerar opções de parcelamento incluindo "à vista"
  const generateInstallmentOptions = () => {
    const options = []

    // No modo fixo, o cliente não escolhe: fixar em 1x não faz sentido, então
    // as opções começam em 2x e o rótulo indica a quantidade exata.
    if (fixed) {
      for (let i = 2; i <= maxInstallments; i++) {
        const installmentValue = calculateInstallmentValue(i)
        options.push({
          value: i,
          label: `${i}x de ${installmentValue}`
        })
      }

      return options
    }

    // Opção à vista (seguindo o mesmo padrão das outras)
    const avistValue = calculateInstallmentValue(1)
    options.push({
      value: 1,
      label: `1x de ${avistValue}`
    })

    // Opções de parcelamento (2x até maxInstallments)
    for (let i = 2; i <= maxInstallments; i++) {
      const installmentValue = calculateInstallmentValue(i)
      options.push({
        value: i,
        label: `Em até ${i}x de ${installmentValue}`
      })
    }

    return options
  }

  const installmentOptions = generateInstallmentOptions()

  return (
    <Controller
      name={fieldName as Path<T>}
      control={control}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <Select
          inputRef={ref}
          helperText={error?.message}
          error={!!error?.message}
          fullWidth={true}
          onChange={onChange}
          sx={{ width: '100%' }}
          value={value || (fixed ? 2 : 1)}
          label={
            fixed
              ? 'Seu cliente pagará sempre em'
              : 'Em quantas parcelas seu cliente pode pagar'
          }
          disabled={loading}
        >
          {installmentOptions.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      )}
    />
  )
}

export default InstallmentSelect
